import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getdata, updatedata, downloadfile } from '../../util/global';
import TableLine, { hasattentionflag } from './TableLine';
import LineForm from './LineForm';

const EMPTYFILTER = { account: "", reviewstatus: "", confidence: "", flag: "", search: "" };

function sortkey(linenumber: any) {
  const parts = String(linenumber).split("-");
  if (parts.length == 1) return { prefix: "", number: Number(parts[0]) };
  return { prefix: parts[0], number: Number(parts[1]) };
}

function fmtamount(value: any) {
  if (value == null) return "";
  return Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const PrudentialReview = () => {
  const batchid = new URLSearchParams(window.location.search).get("batchid");
  const revieweremail = window.APPUSER?.user?.email || "";

  const [batch, setbatch] = useState<any>(null);
  const [linedata, setlinedata] = useState<any[]>([]);
  const [ledgerdata, setledgerdata] = useState<any[]>([]);
  const [filter, setfilter] = useState<any>({ ...EMPTYFILTER });
  const [mode, setmode] = useState<string | null>(null);
  const [formdata, setformdata] = useState<any>({});

  const ledgermap = useMemo(() => {
    const map: any = {};
    for (let i = 0; i < ledgerdata.length; i++) {
      map[ledgerdata[i].ledgername] = ledgerdata[i].tallygroup;
    }
    return map;
  }, [ledgerdata]);

  const sortedlines = useMemo(() => {
    const sorted = linedata.slice();
    sorted.sort((a, b) => {
      const ka = sortkey(a.linenumber);
      const kb = sortkey(b.linenumber);
      if (ka.prefix != kb.prefix) return ka.prefix < kb.prefix ? -1 : 1;
      return ka.number - kb.number;
    });
    return sorted;
  }, [linedata]);

  const counts = useMemo(() => {
    const result = { total: linedata.length, pending: 0, approved: 0, edited: 0, flagged: 0, aiflagged: 0 };
    for (let i = 0; i < linedata.length; i++) {
      const line = linedata[i];
      if (line.reviewstatus == "pending") result.pending++;
      if (line.reviewstatus == "approved") result.approved++;
      if (line.reviewstatus == "edited") result.edited++;
      if (line.reviewstatus == "flagged") result.flagged++;
      if (hasattentionflag(line)) result.aiflagged++;
    }
    return result;
  }, [linedata]);

  const flagcodes = useMemo(() => {
    const codes: string[] = [];
    for (let i = 0; i < linedata.length; i++) {
      const flags = linedata[i].flags || [];
      for (let f = 0; f < flags.length; f++) {
        if (codes.indexOf(flags[f]) < 0) codes.push(flags[f]);
      }
    }
    codes.sort();
    return codes;
  }, [linedata]);

  const filteredlines = useMemo(() => {
    const search = filter.search.trim().toLowerCase();
    const result = [];
    for (let i = 0; i < sortedlines.length; i++) {
      const line = sortedlines[i];
      if (filter.account && line.bankaccount != filter.account) continue;
      if (filter.reviewstatus && line.reviewstatus != filter.reviewstatus) continue;
      if (filter.confidence && line.confidence != filter.confidence) continue;
      if (filter.flag && (line.flags || []).indexOf(filter.flag) < 0) continue;
      if (search) {
        const text = [line.narration, line.vendor, line.finalledger, line.ainotes, line.reviewercomment].join(" ").toLowerCase();
        if (text.indexOf(search) < 0) continue;
      }
      result.push(line);
    }
    return result;
  }, [sortedlines, filter]);

  const fetchdata = async () => {
    const batches = await getdata({ filter: { datatype: "prudentialbatch", _id: batchid } });
    if (Array.isArray(batches) && batches.length > 0) setbatch(batches[0]);
    const lines = await getdata({ filter: { datatype: "prudentialline", _batchid: batchid } });
    if (Array.isArray(lines)) setlinedata(lines);
    const ledgers = await getdata({ filter: { datatype: "prudentialledger", active: true } });
    if (Array.isArray(ledgers)) setledgerdata(ledgers);
  };

  const openline = (line: any, newmode: string) => {
    setformdata({ ...line, ledger: line.finalledger, comment: line.reviewercomment || "" });
    setmode(newmode);
  };

  const approveline = async (line: any) => {
    await updatedata({ data: { _id: line._id, datatype: "prudentialline", reviewstatus: "approved", revieweremail: revieweremail } });
    await fetchdata();
  };

  const resetline = async (line: any) => {
    await updatedata({ data: {
      _id: line._id, datatype: "prudentialline", reviewstatus: "pending",
      overrideledger: null, finalledger: line.suggestedledger, reviewercomment: null, revieweremail: revieweremail,
    } });
    await fetchdata();
  };

  const saveline = async () => {
    const comment = formdata.comment.trim();
    if (mode == "editline") {
      await updatedata({ data: {
        _id: formdata._id, datatype: "prudentialline", reviewstatus: "edited",
        overrideledger: formdata.ledger, finalledger: formdata.ledger,
        reviewercomment: comment || null, revieweremail: revieweremail,
      } });
    } else if (mode == "flagline") {
      if (!comment) {
        alert("A comment is required to flag a line");
        return;
      }
      await updatedata({ data: {
        _id: formdata._id, datatype: "prudentialline", reviewstatus: "flagged",
        reviewercomment: comment, revieweremail: revieweremail,
      } });
    }
    setmode(null);
    await fetchdata();
  };

  const approvehighconfidence = async () => {
    const dataset = [];
    for (let i = 0; i < linedata.length; i++) {
      const line = linedata[i];
      if (line.reviewstatus != "pending" || line.confidence != "high" || hasattentionflag(line)) continue;
      dataset.push({ _id: line._id, datatype: "prudentialline", reviewstatus: "approved", revieweremail: revieweremail });
    }
    if (dataset.length == 0) {
      alert("No pending high-confidence lines without attention flags");
      return;
    }
    if (!confirm("Approve " + dataset.length + " pending high-confidence lines with no attention flags? (info flags such as datedrift are allowed)")) return;
    await updatedata({ dataset: dataset });
    await fetchdata();
  };

  const exportbatch = async () => {
    const result = await downloadfile({ batchid: batchid }, "/prudentialexport");
    if (result && result.error) alert(result.error);
  };

  useEffect(() => { fetchdata(); }, []);

  const reconbadges = [];
  for (let a = 0; batch && batch.accounts && a < batch.accounts.length; a++) {
    const account = batch.accounts[a];
    reconbadges.push(
      <span key={account.bankaccount} className={"recon-badge recon-badge-" + account.reconstatus}>
        {account.currency} {account.bankaccount}: {account.reconstatus} · opening {fmtamount(account.openingbalance)} · closing {fmtamount(account.closingbalance)}
      </span>
    );
  }

  const accountoptions = [];
  for (let a = 0; batch && batch.accounts && a < batch.accounts.length; a++) {
    const account = batch.accounts[a];
    accountoptions.push(<option key={account.bankaccount} value={account.bankaccount}>{account.currency} {account.bankaccount}</option>);
  }

  const flagoptions = [];
  for (let i = 0; i < flagcodes.length; i++) {
    flagoptions.push(<option key={flagcodes[i]} value={flagcodes[i]}>{flagcodes[i]}</option>);
  }

  return (
    <>
      <div className="page-header">
        <h2>Review {batch ? batch.clientname + " · " + batch.period : ""}</h2>
        <div className="page-actions">
          <Link to="/dashboard">Back to batches</Link>
          <button className="btn-primary" onClick={exportbatch}>Export Excel</button>
        </div>
      </div>

      <div className="review-header">
        <div className="review-file">{batch ? batch.filename : ""}</div>
        <div className="review-recon">{reconbadges}</div>
        <div className="review-counts">
          Total {counts.total} · Pending {counts.pending} · Approved {counts.approved} · Edited {counts.edited} · Flagged {counts.flagged} · AI-flagged lines: {counts.aiflagged}
        </div>
      </div>

      <div className="filter-row">
        <select value={filter.account} onChange={(e) => setfilter({ ...filter, account: e.target.value })}>
          <option value="">All accounts</option>
          {accountoptions}
        </select>
        <select value={filter.reviewstatus} onChange={(e) => setfilter({ ...filter, reviewstatus: e.target.value })}>
          <option value="">All statuses</option>
          <option value="pending">pending</option>
          <option value="approved">approved</option>
          <option value="edited">edited</option>
          <option value="flagged">flagged</option>
        </select>
        <select value={filter.confidence} onChange={(e) => setfilter({ ...filter, confidence: e.target.value })}>
          <option value="">All confidence</option>
          <option value="high">high</option>
          <option value="medium">medium</option>
          <option value="low">low</option>
        </select>
        <select value={filter.flag} onChange={(e) => setfilter({ ...filter, flag: e.target.value })}>
          <option value="">All flags</option>
          {flagoptions}
        </select>
        <input
          type="text"
          placeholder="Search narration, vendor, ledger, notes"
          value={filter.search}
          onChange={(e) => setfilter({ ...filter, search: e.target.value })}
        />
        <button className="btn-primary" onClick={approvehighconfidence}>Approve all high-confidence lines with no attention flags</button>
      </div>

      <TableLine
        linedata={filteredlines}
        ledgermap={ledgermap}
        openline={openline}
        approveline={approveline}
        resetline={resetline}
      />

      <LineForm
        mode={mode}
        setmode={setmode}
        formdata={formdata}
        setformdata={setformdata}
        saveline={saveline}
        ledgerdata={ledgerdata}
        ledgermap={ledgermap}
      />
    </>
  );
};

export default PrudentialReview;
